import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './auth/AuthContext'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { ToastProvider } from './components/Toast'
import { ThemeProvider } from './theme/ThemeContext'
import { HomePage } from './pages/HomePage'

const IliaPage = lazy(() => import('./pages/IliaPage').then((m) => ({ default: m.IliaPage })))
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('./pages/RegisterPage').then((m) => ({ default: m.RegisterPage })))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })))
const SubscribePage = lazy(() => import('./pages/SubscribePage').then((m) => ({ default: m.SubscribePage })))
const PlanDetailPage = lazy(() => import('./pages/PlanDetailPage').then((m) => ({ default: m.PlanDetailPage })))
const BranchDetailPage = lazy(() => import('./pages/BranchDetailPage').then((m) => ({ default: m.BranchDetailPage })))
const LeistungenPage = lazy(() => import('./pages/LeistungenPage').then((m) => ({ default: m.LeistungenPage })))
const FilialenPage = lazy(() => import('./pages/FilialenPage').then((m) => ({ default: m.FilialenPage })))
const DisplayBoardPage = lazy(() => import('./pages/DisplayBoardPage').then((m) => ({ default: m.DisplayBoardPage })))
const BookingPage = lazy(() => import('./pages/BookingPage').then((m) => ({ default: m.BookingPage })))
const BookSuccessPage = lazy(() => import('./pages/BookingPage').then((m) => ({ default: m.BookSuccessPage })))
const BookActionPage = lazy(() => import('./pages/BookActionPage').then((m) => ({ default: m.BookActionPage })))
const StylesPage = lazy(() => import('./pages/StylesPage').then((m) => ({ default: m.StylesPage })))
const ShopPage = lazy(() => import('./pages/ShopPage').then((m) => ({ default: m.ShopPage })))
const ClubPage = lazy(() => import('./pages/ClubPage').then((m) => ({ default: m.ClubPage })))
const GiftCardsPage = lazy(() => import('./pages/GiftCardsPage').then((m) => ({ default: m.GiftCardsPage })))
const GiftCardSuccessPage = lazy(() => import('./pages/GiftCardsPage').then((m) => ({ default: m.GiftCardSuccessPage })))
const ContactPage = lazy(() => import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })))
const FaqPage = lazy(() => import('./pages/FaqPage').then((m) => ({ default: m.FaqPage })))
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })))
const AdminDashboardPage = lazy(() => import('./pages/DashboardPages').then((m) => ({ default: m.AdminDashboardPage })))
const CustomerDashboardPage = lazy(() => import('./pages/DashboardPages').then((m) => ({ default: m.CustomerDashboardPage })))
const EmployeeDashboardPage = lazy(() => import('./pages/DashboardPages').then((m) => ({ default: m.EmployeeDashboardPage })))
const SalonDashboardPage = lazy(() => import('./pages/DashboardPages').then((m) => ({ default: m.SalonDashboardPage })))
const SalonPreviewPage = lazy(() => import('./pages/SalonPreviewPage').then((m) => ({ default: m.SalonPreviewPage })))
const SalonPreviewLegacyRedirect = lazy(() => import('./pages/SalonPreviewPage').then((m) => ({ default: m.SalonPreviewLegacyRedirect })))
const SalonPublicHomePage = lazy(() => import('./pages/SalonPublicHomePage').then((m) => ({ default: m.SalonPublicHomePage })))
const CustomerLoyaltyPage = lazy(() => import('./pages/CustomerLoyaltyPage').then((m) => ({ default: m.CustomerLoyaltyPage })))
const PublicQrPage = lazy(() => import('./pages/PublicQrPage').then((m) => ({ default: m.PublicQrPage })))
const TenantPublicLayout = lazy(() => import('./components/TenantPublicLayout').then((m) => ({ default: m.TenantPublicLayout })))

const queryClient = new QueryClient()

/** Keep branch detail from swallowing reserved public paths. */
function BranchOrRedirect() {
  const { slug = '' } = useParams()
  if (slug === 'leistungen' || slug === 'services') {
    return <Navigate to="/leistungen" replace />
  }
  if (slug === 'filialen' || slug === 'unsere-filialen') {
    return <Navigate to="/filialen" replace />
  }
  return <BranchDetailPage />
}

/** Old /s/{slug} links (Instagram, emails) → short /{slug}. */
function LegacySalonSiteRedirect() {
  const { slug = '' } = useParams()
  const loc = useLocation()
  const rest = loc.pathname.replace(/^\/s\/[^/]+/, '')
  return <Navigate to={`/${slug}${rest}${loc.search}${loc.hash}`} replace />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <Suspense fallback={<div className="section"><div className="container"><div className="skeleton" style={{ height: 160 }} /></div></div>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/filialen" element={<FilialenPage />} />
              <Route path="/filialen/display" element={<DisplayBoardPage />} />
              <Route path="/filialen/tv" element={<DisplayBoardPage />} />
              <Route path="/filialen/*" element={<FilialenPage />} />
              <Route path="/unsere-filialen" element={<Navigate to="/filialen" replace />} />
              <Route path="/display" element={<DisplayBoardPage />} />
              <Route path="/tv" element={<DisplayBoardPage />} />
              <Route path="/ilia-barbershop" element={<IliaPage />} />
              <Route path="/ilia-barbershop/leistungen" element={<Navigate to="/leistungen" replace />} />
              <Route path="/leistungen" element={<LeistungenPage />} />
              <Route path="/leistungen/*" element={<LeistungenPage />} />
              <Route path="/styles" element={<StylesPage />} />
              <Route path="/stile" element={<Navigate to="/styles" replace />} />
              <Route path="/services" element={<Navigate to="/leistungen" replace />} />
              <Route path="/services/*" element={<Navigate to="/leistungen" replace />} />
              <Route path="/branches/:slug" element={<BranchOrRedirect />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/book/success" element={<BookSuccessPage />} />
              <Route path="/book/confirm/:token" element={<BookActionPage mode="confirm" />} />
              <Route path="/book/cancel/:token" element={<BookActionPage mode="cancel" />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/plans/:planId" element={<PlanDetailPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/cart" element={<ShopPage />} />
              <Route path="/memberships" element={<Navigate to="/club" replace />} />
              <Route path="/club" element={<ClubPage />} />
              <Route path="/qr" element={<PublicQrPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/kontakt" element={<ContactPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/fragen" element={<FaqPage />} />
              <Route path="/impressum" element={<LegalPage />} />
              <Route path="/datenschutz" element={<LegalPage />} />
              <Route path="/agb" element={<LegalPage />} />
              <Route path="/privacy" element={<Navigate to="/datenschutz" replace />} />
              <Route path="/terms" element={<Navigate to="/agb" replace />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/subscriptions" element={<Navigate to="/club" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              <Route element={<ProtectedRoute roles={['ROLE_SUPER_ADMIN', 'ROLE_PLATFORM_MANAGER']} />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
              </Route>
              <Route element={<ProtectedRoute roles={['ROLE_SALON_OWNER', 'ROLE_BRANCH_MANAGER', 'ROLE_ACCOUNTANT']} />}>
                <Route path="/salon" element={<SalonDashboardPage />} />
                <Route path="/salon/:section" element={<SalonDashboardPage />} />
              </Route>
              <Route path="/preview/:slug" element={<SalonPreviewPage />} />
              <Route path="/preview/salon/:token" element={<SalonPreviewLegacyRedirect />} />
              <Route path="/s/:slug/*" element={<LegacySalonSiteRedirect />} />
              <Route element={<ProtectedRoute roles={['ROLE_BARBER', 'ROLE_RECEPTIONIST']} />}>
                <Route path="/employee" element={<EmployeeDashboardPage />} />
              </Route>
              <Route element={<ProtectedRoute roles={['ROLE_CUSTOMER']} />}>
                <Route path="/account" element={<CustomerDashboardPage />} />
                <Route path="/account/loyalty" element={<CustomerLoyaltyPage />} />
              </Route>
              <Route path="/:slug" element={<TenantPublicLayout />}>
                <Route index element={<SalonPublicHomePage />} />
                <Route path="book" element={<BookingPage />} />
                <Route path="book/success" element={<BookSuccessPage />} />
                <Route path="shop" element={<ShopPage />} />
                <Route path="cart" element={<ShopPage />} />
                <Route path="club" element={<ClubPage />} />
                <Route path="gift-cards" element={<GiftCardsPage />} />
                <Route path="gift-cards/success" element={<GiftCardSuccessPage />} />
                <Route path="display" element={<DisplayBoardPage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="forgot-password" element={<ForgotPasswordPage />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
                <Route path="impressum" element={<LegalPage />} />
                <Route path="datenschutz" element={<LegalPage />} />
                <Route path="agb" element={<LegalPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
